import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ArrowLeft, ExternalLink, Mail } from "lucide-react";
import { useCuratedPostBySlug } from "@/hooks/useCuratedPosts";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

const CuratedPostDetail = () => {
  const { slug } = useParams();
  const { data: post, isLoading } = useCuratedPostBySlug(slug);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <SiteHeader />
        <main className="pt-32 container mx-auto px-6">
          <p className="text-muted-foreground">Loading…</p>
        </main>
      </div>
    );
  }

  if (!post || post.status !== "published") {
    return (
      <div className="min-h-screen bg-background">
        <SiteHeader />
        <main className="pt-32 container mx-auto px-6">
          <h1 className="font-display text-2xl font-bold mb-2">Post not found</h1>
          <Link to="/archive" className="text-primary hover:underline">← Back to archive</Link>
        </main>
      </div>
    );
  }

  // Claim contact email is admin-only (not exposed to clients). Always route
  // public attribution requests through our shared inbox.
  const claimHref = `mailto:emergingmrkt@gmail.com?subject=${encodeURIComponent(
    `Attribution request: ${post.title}`,
  )}`;

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Helmet>
        <title>{post.title} · Curated · The Emerging Market</title>
        <meta name="description" content={post.summary ?? `Curated post crediting ${post.source_org_name}.`} />
        <link rel="canonical" href={post.source_url} />
      </Helmet>
      <SiteHeader />
      <main className="pt-28 pb-20">
        <article className="container mx-auto px-6 max-w-3xl">
          <Link
            to="/archive"
            className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-6"
          >
            <ArrowLeft className="h-4 w-4" /> Archive
          </Link>

          <div className="flex flex-wrap gap-2 mb-3">
            <Badge className="bg-accent/15 text-accent-foreground border border-accent/30">
              EM Curated
            </Badge>
            {post.category && <Badge variant="outline">{post.category}</Badge>}
            {post.tags?.map((t) => (
              <Badge key={t} variant="outline">{t}</Badge>
            ))}
          </div>

          <h1 className="font-display text-3xl sm:text-4xl font-bold leading-tight mb-3">
            {post.title}
          </h1>
          {post.summary && (
            <p className="text-lg text-muted-foreground mb-6">{post.summary}</p>
          )}

          {/* Source credit — always visible, links back to original */}
          <div className="rounded-xl border border-border bg-card/50 p-4 mb-8 flex flex-col sm:flex-row sm:items-center gap-3">
            {post.source_logo_url && (
              <img
                src={post.source_logo_url}
                alt={`${post.source_org_name} logo`}
                className="h-10 w-10 rounded-md object-contain bg-background border border-border"
              />
            )}
            <div className="flex-1 min-w-0">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">
                Originally from
              </p>
              <a
                href={post.source_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 font-semibold hover:text-primary"
              >
                {post.source_org_name}
                <ExternalLink className="h-3.5 w-3.5" />
              </a>
              {post.attribution_note && (
                <p className="text-xs text-muted-foreground mt-1">{post.attribution_note}</p>
              )}
            </div>
            <Button asChild variant="outline" size="sm">
              <a href={post.source_url} target="_blank" rel="noopener noreferrer">
                Read original
              </a>
            </Button>
          </div>

          {post.cover_image_url && (
            <img
              src={post.cover_image_url}
              alt={post.title}
              className="w-full rounded-xl mb-8 border border-border"
            />
          )}

          {post.body_md && (
            <div className="prose prose-invert prose-sm sm:prose-base max-w-none">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{post.body_md}</ReactMarkdown>
            </div>
          )}

          <div className="mt-10 pt-6 border-t border-border text-sm text-muted-foreground">
            <p className="mb-2">
              We share this work to credit and amplify the people doing it. If you represent{" "}
              <strong>{post.source_org_name}</strong> and want to claim, update, or request removal
              of this post:
            </p>
            <Button asChild variant="outline" size="sm">
              <a href={claimHref}>
                <Mail className="h-3.5 w-3.5 mr-2" /> Claim or request changes
              </a>
            </Button>
          </div>
        </article>
      </main>
      <SiteFooter />
    </div>
  );
};

export default CuratedPostDetail;